import styled from "@emotion/styled";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import NotificationsCubit from "src/state/NotificationsCubit/NotificationsCubit";
import translate from "src/lib/translate";
import { FeatureFlags } from "src/lib/featureFlags";
import { useBloc } from "src/state/state";
import {
  AppPopup,
  useAppQueryPopup
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";

const Button = styled.button`
  background: var(--Secondary-Dark-Cream, #f2efe7);
  border-radius: 50%;
  aspect-ratio: 1;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--Secondary-Cream, #e9e5da);
  }

  &:active {
    background: var(--Secondary-Dark-Cream-Pressed, #dedad0);
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
  }

  &[data-desktop-nav="true"] {
    margin-left: 1rem;
  }

  @media screen and (min-width: 730px) {
    &[data-mobile="true"] {
      display: none;
    }
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -0.2rem;
  right: -0.2rem;
  min-width: 1.2rem;
  height: 1.2rem;
  padding: 0 0.25rem;
  border-radius: 2rem;
  background: var(--Primary-ErrorRed, #bf1902);
  color: white;
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1.2rem;
  text-align: center;
  border: 1px solid var(--Greys-Light-Gray, #e6e3db);
  transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1);

  &[aria-hidden="true"] {
    transform: scale(0);
  }
`;

const NotificationBell: FC<{ desktopNav?: boolean; mobile?: boolean }> = ({
  desktopNav,
  mobile
}) => {
  const [, { isConversationsEnabled }] = useBloc(FeatureFlags);
  const [{ unreadCount }] = useBlocNext(NotificationsCubit);
  const [openNotifications] = useAppQueryPopup(AppPopup.notifications);

  if (!isConversationsEnabled) {
    return null;
  }

  const label =
    unreadCount > 0
      ? translate("notifications.bell.labelWithCount", { count: unreadCount })
      : translate("notifications.bell.label");

  return (
    <Button
      onClick={() => openNotifications()}
      aria-label={label}
      data-desktop-nav={desktopNav ? "true" : "false"}
      data-mobile={mobile ? "true" : "false"}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="17"
        height="17"
        fill="none"
        viewBox="0 0 17 17"
        aria-hidden="true"
      >
        <path
          stroke="#212121"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
          d="M10.18 12.751a2 2 0 0 1-4 .057m3.076-8.552a1.667 1.667 0 1 0-2.395.034m5.244 3.233c-.013-.92-.447-1.795-1.206-2.434-.76-.64-1.782-.99-2.843-.975s-2.073.395-2.814 1.055c-.74.661-1.15 1.549-1.136 2.468.022 1.521-.34 2.639-.799 3.442-.524.914-.785 1.372-.773 1.48.014.124.037.163.138.235.089.063.53.056 1.414.044l8.188-.117c.883-.012 1.324-.019 1.411-.084.1-.075.121-.115.132-.239.009-.108-.266-.558-.815-1.458-.482-.789-.875-1.896-.897-3.417"
        />
      </svg>
      <Badge aria-hidden={unreadCount === 0}>
        {unreadCount < 10 ? unreadCount : "9+"}
      </Badge>
    </Button>
  );
};

export default NotificationBell;
